import { Component, OnInit } from '@angular/core';
import { ICON, ICON_STYLE } from '@core/util/icon';
import { UserMatterViewHistory } from '@models/userViewHistory';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { NavigationService } from '@services/nav/nav.service';
import { UrlService } from '@services/url/url.service';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { RecentlyViewedDispatch } from '@state/recently-viewed/recently-viewed.dispatch';
import { RecentlyViewedSelector } from '@state/recently-viewed/recently-viewed.selector';
import { firstValueFrom, Observable } from 'rxjs';
import { TestHooks } from './last-opened-tabs.hooks';
import { Logger } from '@core/logger';
import { LeapApiService } from '../../../shared/api-repos/leap/leap-api.service';

@UntilDestroy()
@Component({
	selector: 'app-last-opened-tabs',
	templateUrl: './last-opened-tabs.component.html',
	styleUrls: ['./last-opened-tabs.component.scss'],
	standalone: false,
})
export class LastOpenedTabsComponent implements OnInit {
	testHooks = TestHooks;
	private _tabs: UserMatterViewHistory[] = [];
	@Select(RecentlyViewedSelector.getTabs) tabs$: Observable<
		UserMatterViewHistory[]
	>;
	@SelectSnapshot(SelectedMatter.id) selectedId: string;
	@SelectSnapshot(SelectedMatter.matterNumber) number: string;
	@SelectSnapshot(SelectedMatter.matterName) name: string;
	@SelectSnapshot(SelectedMatter.isLeapMatter) isLeapMatter: boolean;

	public ICON = ICON;
	public ICON_STYLE = ICON_STYLE;

	constructor(
		private navigationService: NavigationService,
		private recentlyViewedDispatch: RecentlyViewedDispatch,
		private analytics: AnalyticsService,
		private leapApiService: LeapApiService,
	) {}

	ngOnInit(): void {
		this.tabs$.pipe(untilDestroyed(this)).subscribe((tabs) => {
			this._tabs = tabs;
		});
	}

	goToFile(matter: UserMatterViewHistory): void {
		this.analytics.logEvent(AnalyticsEvent.LAST_OPENED_TABS_FILE_CLICK, {
			matterId: matter.id,
		});
		this.navigationService.goToMatter(matter.id);
	}

	remove(ev: Event, matterId: string, index: number): void {
		ev.stopPropagation();
		ev.preventDefault();

		if (this.isLeapMatter && matterId === this.selectedId) {
			Logger.info(
				`Closed tab for matter ${matterId}. Sending changes to LEAP for matter ${matterId}.`,
			);
			firstValueFrom(this.leapApiService.sendToLeap(matterId));
		}

		this.recentlyViewedDispatch.HideFromTabs(matterId);

		if (this.selectedId === matterId) {
			let redirectUrl = UrlService.Dashboard();
			if (this._tabs.length > 1) {
				let id;
				if (index + 1 < this._tabs.length) {
					id = this._tabs[index + 1]?.id;
				} else {
					id = this._tabs[index - 1]?.id;
				}

				if (id) {
					redirectUrl = UrlService.Matter(id);
				}
			}

			this.navigationService.goToUrl(redirectUrl);
		}
	}

	unpin(ev: Event, matterId: string): void {
		ev.stopPropagation();
		ev.preventDefault();
		this.recentlyViewedDispatch.Pin(matterId, false);
	}
}
